import { cn } from '@/lib/utils';
import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { router } from 'router';

import { useAuth, useChatSession, useConfig } from '@chainlit/react-client';

import ChatSettingsModal from './components/ChatSettings';
import { ThemeProvider } from './components/ThemeProvider';
import { Loader } from '@/components/Loader';
import { Toaster } from '@/components/ui/sonner';

import { userEnvState } from 'state/user';

import { isAdminConsole } from '@/AppMode';

declare global {
  interface Window {
    cl_shadowRootElement?: HTMLDivElement;
    transports?: string[];
    theme?: {
      light: Record<string, string>;
      dark: Record<string, string>;
    };
    __APP_MODE__?: string; // 'legalchat' | 'legalgraph'
    __DEFAULT_LANGUAGE__?: string; // 'fr' | 'en' | 'de' — env DEFAULT_LANGUAGE
    __DEFAULT_MARKET__?: string; // 'fr' | 'eu' — env DEFAULT_MARKET
  }
}



function App() {
  const { config } = useConfig();

  const { isAuthenticated, data, isReady } = useAuth();
  const userEnv = useRecoilValue(userEnvState);
  const { connect, chatProfile, setChatProfile } = useChatSession();

  const configLoaded = !!config;

  const chatProfileOk = configLoaded
    ? config.chatProfiles.length
      ? !!chatProfile
      : true
    : false;

  useEffect(() => {
    // ── MODE LEGALGRAPH / console master : jamais de connexion WebSocket ──
    if (window.__APP_MODE__ === 'legalgraph' || isAdminConsole()) return;

    if (!isAuthenticated || !isReady || !chatProfileOk) {
      return;
    }

    connect({
      transports: window.transports,
      userEnv
    });
  }, [userEnv, isAuthenticated, connect, isReady, chatProfileOk]);

  useEffect(() => {
    // ── MODE LEGALGRAPH / console master : pas de chat profiles ───
    if (window.__APP_MODE__ === 'legalgraph' || isAdminConsole()) return;

    if (
      !configLoaded ||
      !config ||
      !config.chatProfiles?.length ||
      chatProfile
    ) {
      return;
    }

    const defaultChatProfile = config.chatProfiles.find(
      (profile) => profile.default
    );

    if (defaultChatProfile) {
      setChatProfile(defaultChatProfile.name);
    } else {
      setChatProfile(config.chatProfiles[0].name);
    }
  }, [configLoaded, config, chatProfile, setChatProfile]);

  // ── MODE LEGALGRAPH : pas de loader bloquant sur configLoaded ──
  if (window.__APP_MODE__ !== 'legalgraph' && !configLoaded && isAuthenticated) return null;

  return (
    <ThemeProvider
      storageKey="vite-ui-theme"
      defaultTheme={data?.default_theme}
    >
      <Toaster richColors className="toast" position="top-right" />

      {/* ChatSettingsModal inutile en mode legalgraph mais inoffensif */}
      <ChatSettingsModal />
      <RouterProvider router={router} />

      {/* Loader de démarrage — masqué en mode legalgraph (isReady jamais true sans socket) */}
      {window.__APP_MODE__ !== 'legalgraph' && (
        <div
          className={cn(
            'bg-[hsl(var(--background))] flex items-center justify-center fixed size-full p-2 top-0',
            isReady && 'hidden'
          )}
        >
          <Loader className="!size-6" />
        </div>
      )}
    </ThemeProvider>
  );
}

export default App;